import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";



function PostMeta({ date, author, categories, className = "" }: { date?: string, author?: any, categories?: any[], className?: string }) {
  return (
    <div data-section-id="15151" className={`flex flex-wrap items-center gap-4 text-sm text-muted-foreground font-default ${className}`}>
      {date && (
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-secondary" />
          <span>{new Date(date).toLocaleDateString("vi-VN")}</span>
        </div>
      )}
      {author && (
        <div className="flex items-center gap-1.5">
          <User className="w-4 h-4 text-secondary" />
          <span>{author.name}</span>
        </div>
      )}
      {categories && categories.length > 0 && (
        <div className="flex items-center gap-1.5">
          <Tag className="w-4 h-4 text-secondary" />
          <span className="capitalize">{categories[0].name}</span>
        </div>
      )}
    </div>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-8 animate-pulse">
        <div className="h-12 bg-muted rounded-none w-3/4 mx-auto" />
        <div className="h-6 bg-muted rounded-none w-1/2 mx-auto" />
        <div className="aspect-video bg-muted rounded-none w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-none w-full" />
          <div className="h-4 bg-muted rounded-none w-full" />
          <div className="h-4 bg-muted rounded-none w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive mb-4">Lỗi khi tải bài viết: {error}</p>
        <Button onClick={() => refetch()} variant="outline" className="rounded-none">Thử Lại</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Không tìm thấy bài viết.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="text-center mb-10 md:mb-14">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-default font-bold text-primary mb-6 leading-tight">
          {post.title}
        </h1>
        <PostMeta 
          date={post.date} 
          author={post.author} 
          categories={post.categories} 
          className="justify-center"
        />
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 md:mb-16 rounded-none overflow-hidden border border-border/40 shadow-sm bg-card">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover aspect-video"
          />
        </div>
      )}

      {}
      <div 
        className="
          prose prose-lg max-w-none 
          font-default text-foreground/90 leading-relaxed
          [&_h2]:font-default [&_h2]:text-3xl [&_h2]:text-primary [&_h2]:mt-12 [&_h2]:mb-6
          [&_h3]:font-default [&_h3]:text-2xl [&_h3]:text-foreground [&_h3]:mt-10 [&_h3]:mb-4
          [&_p]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_li]:mb-2
          [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-secondary
          [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground
          [&_img]:rounded-none [&_img]:my-8
        "
        dangerouslySetInnerHTML={{ __html: post.content || "" }} 
      />

      <Separator className="my-12 md:my-16" />

      {}
      {post.author && (
        <div className="bg-card border border-border rounded-none p-8 flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
          <div className="shrink-0">
            {post.author.avatar ? (
               <img 
                 src={Object.values(post.author.avatar)[0] || "/img/placeholder_avatar.png"} 
                 alt={post.author.name}
                 className="w-20 h-20 rounded-none border-2 border-primary object-cover"
               />
            ) : (
              <div className="w-20 h-20 rounded-none bg-muted flex items-center justify-center border-2 border-primary">
                <User className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-default font-bold text-foreground mb-2">
              Về {post.author.name}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              {}
              Thành viên đội ngũ Vietomy. Tâm huyết trong việc chia sẻ kiến thức tài chính, giúp gia đình Việt xây dựng tương lai thịnh vượng tại Hoa Kỳ.
            </p>
            {post.author.link && (
              <Link to={post.author.link} className="text-primary text-sm font-semibold hover:underline">
                Xem tất cả bài viết của {post.author.name}
              </Link>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 bg-muted rounded-none animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="bg-card border-border rounded-none overflow-hidden flex flex-col h-full hover:border-secondary/50 transition-colors duration-300">
          {post.featuredImage ? (
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          ) : (
            <div className="aspect-[4/3] bg-muted flex items-center justify-center">
              <Tag className="w-12 h-12 text-muted-foreground/30" />
            </div>
          )}
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>{post.date ? new Date(post.date).toLocaleDateString("vi-VN") : ""}</span>
              {post.categories?.[0] && <span className="text-secondary uppercase tracking-wider font-bold">{post.categories[0].name}</span>}
            </div>
            <CardTitle className="font-default text-xl leading-tight line-clamp-2 text-foreground">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <CardDescription className="line-clamp-3 text-sm">
              <div dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
            </CardDescription>
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full group rounded-none border-primary/30 hover:bg-primary hover:text-primary-foreground">
                Đọc Bài Viết
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const [mainQuery, setMainQuery] = useState<WP_Query | null>(null);
  const [relatedQuery, setRelatedQuery] = useState<WP_Query | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const client = (window as any).wvcClient;
      const currentParams = client ? client.getCurrentWPQueryParams() : { post_type: 'post', p: 1 }; 
      
      setMainQuery(new WP_Query(currentParams));
      
      
      setRelatedQuery(new WP_Query({
        post_type: 'post',
        posts_per_page: 3,
        ignore_sticky_posts: true,
        orderby: 'date',
        order: 'DESC'
      }));
    }
  }, []);

  if (!mainQuery || !relatedQuery) return null;

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        
        {}
        <PostsProvider wp_query={mainQuery} className="mb-20">
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-3xl md:text-4xl font-default font-bold text-primary">
              Bài Viết Liên Quan
            </h2>
            <Link to="#">
              <Button variant="ghost" className="hidden md:flex text-muted-foreground hover:text-primary rounded-none">
                Xem Tất Cả Bài Viết <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>

          <div className="mt-8 md:hidden text-center">
            <Link to="#">
              <Button variant="outline" className="w-full rounded-none">
                Xem Tất Cả Bài Viết
              </Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}